import {
  Button,
  ButtonGroup,
  Input,
  Text,
  Grid,
  Card,
  useTheme,
  Spacer
} from "@geist-ui/core";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import NumberFormat from "react-number-format";

interface CostWidgetProps {
  pessimistic_szenario: number;
  medium_szenario: number;
  optimistic_szenario: number;
  management_fee: number;
  upfront_fee: number;
  carry_fee_pessimistic: number;
  carry_fee_medium: number;
  carry_fee_optimistic: number;
  years: number;
}

export default function CostWidget(props: CostWidgetProps) {
  const theme = useTheme();
  var height = 100;
  const max_height = 200;
  const width = "50px";
  const static_costs = props.management_fee * props.years + props.upfront_fee;
  const overflow = height * props.medium_szenario > max_height;
  if (overflow) height = max_height / props.medium_szenario;

  const [investment_amount, set_investment_amount] = useState(10000);
  const [active_szenario, set_active_szenario] = useState(1);
  const [inputbar_height, set_inputbar_height] = useState(height);
  const [payoutbar_height, set_payoutbar_height] = useState(
    overflow ? max_height : height * props.medium_szenario
  );
  const [cost_bar_height, set_cost_bar_height] = useState(
    height * (props.carry_fee_medium + static_costs)
  );
  const [carry_cost_total, set_carry_cost_total] = useState(
    investment_amount * props.carry_fee_medium
  );
  const upfront_fee_total = investment_amount * props.upfront_fee;
  const management_fee_total =
    investment_amount * props.management_fee * props.years;
  const [netto_return, set_netto_return] = useState(
    investment_amount * props.medium_szenario
  );
  const [total_return, set_total_return] = useState(
    investment_amount * props.medium_szenario +
      carry_cost_total +
      upfront_fee_total +
      management_fee_total
  );

  useEffect(() => {
    var returns = upfront_fee_total + management_fee_total;
    var carry_cost_total = 0;
    var performance = props.medium_szenario;

    var payoutbar_height = 0;
    var inputbar_height = height;
    var carry = 0;

    switch (active_szenario) {
      case 0:
        performance = props.pessimistic_szenario;
        carry = props.carry_fee_pessimistic;
        set_netto_return(investment_amount * props.pessimistic_szenario);
        carry_cost_total = investment_amount * props.carry_fee_pessimistic;
        returns += investment_amount * props.pessimistic_szenario;
        break;
      case 1:
        performance = props.medium_szenario;
        carry = props.carry_fee_medium;
        set_netto_return(investment_amount * props.medium_szenario);
        carry_cost_total = investment_amount * props.carry_fee_medium;
        returns += investment_amount * props.medium_szenario;
        break;
      case 2:
        performance = props.optimistic_szenario;
        carry = props.carry_fee_optimistic;
        set_netto_return(investment_amount * props.optimistic_szenario);
        carry_cost_total = investment_amount * props.carry_fee_optimistic;
        returns += investment_amount * props.optimistic_szenario;
        break;
    }
    payoutbar_height = inputbar_height * performance;
    const overflow = payoutbar_height > max_height;

    if (overflow) {
      payoutbar_height = max_height;
      inputbar_height = max_height / performance;
    }
    set_cost_bar_height(inputbar_height * (static_costs + carry));
    set_payoutbar_height(payoutbar_height);
    set_inputbar_height(inputbar_height);
    returns += carry_cost_total;
    set_carry_cost_total(carry_cost_total);
    set_total_return(returns);
  }, [
    active_szenario,
    investment_amount,
    height,
    management_fee_total,
    props.carry_fee_medium,
    props.carry_fee_optimistic,
    props.carry_fee_pessimistic,
    props.medium_szenario,
    props.optimistic_szenario,
    props.pessimistic_szenario,
    static_costs,
    upfront_fee_total,
  ]);

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "700px",
        margin: "auto",
        marginTop: "100px",
      }}
    >
      <Text h4>Kostenübersicht</Text>
      <Grid.Container gap={2} justify="center" marginTop="30px">
        <Grid xs={8}>
          <Card width="100%" style={{ textAlign: "center" }}>
            <Text h4 margin={0}>
              {(props.upfront_fee * 100).toLocaleString("de-de")}%
            </Text>
            <Text p margin={0}>Initialkosten</Text>
          </Card>
        </Grid>
        <Grid xs={8}>
          <Card width="100%" style={{ textAlign: "center" }}>
            <Text h4 margin={0}>
              {props.management_fee * 100}% p.a.
            </Text>
            <Text p margin={0}>Managementgebühr</Text>
          </Card>
        </Grid>
        <Grid xs={8}>
          <Card width="100%" style={{ textAlign: "center" }}>
            <Text h6 style={{ fontWeight: 600 }} margin={0}>
              Carry
            </Text>
            <Text p margin={0} style={{ textAlign: "center" }}>
              Nur wenn du profitierst, profitieren wir.
            </Text>
          </Card>
        </Grid>
      </Grid.Container>

      <div style={{ marginTop: "40px" }}>
        <Card>
          <Text p b>
            Die dargestellten Renditen sind nicht garantiert und können geringer
            ausfallen, ferner kann es zum Verlust des eingesetzten Kapitals
            kommen.
          </Text>
        </Card>
        <Spacer h={1} />
        <Text p b>
          Mit dem folgenden Rechner kannst Du die Kosten unter Annahme der
          verschiedenen Szenarien nachvollziehen.
        </Text>
      </div>
      <Grid.Container gap={2} marginTop="30px">
        <Grid xs={24} md={8}>
          <div style={{ width: '100%' }}>
            <Text b>Anlagebetrag</Text>
            <Spacer h={.5} />
            <NumberFormat
              value={investment_amount || ""}
              customInput={Input}
              suffix={" €"}
              type="text"
              decimalSeparator={","}
              thousandSeparator={"."}
              onValueChange={(value) => {
                set_investment_amount(Number(value.value));
              }}
              width="100%"
            />
          </div>
        </Grid>
        <Grid xs={24} md={16}>
          <Text b>Wähle ein Szenario</Text>
          <Spacer h={.5} />
          <ButtonGroup>
            <Button
              type={active_szenario === 0 ? "secondary-light" : "default"}
              onClick={() => set_active_szenario(0)}
              style={{
                 backgroundColor: active_szenario === 0 ? theme.palette.foreground : theme.palette.accents_1,
                 color: active_szenario === 0 ? theme.palette.background : theme.palette.foreground
              }}
            >
              Negativ ({props.pessimistic_szenario}x)
            </Button>
            <Button
              type={active_szenario === 1 ? "secondary-light" : "default"}
              onClick={() => set_active_szenario(1)}
              style={{
                 backgroundColor: active_szenario === 1 ? theme.palette.foreground : theme.palette.accents_1,
                 color: active_szenario === 1 ? theme.palette.background : theme.palette.foreground
              }}
            >
              Mittleres ({props.medium_szenario}x)
            </Button>
            <Button
              type={active_szenario === 2 ? "secondary-light" : "default"}
              onClick={() => set_active_szenario(2)}
              style={{
                 backgroundColor: active_szenario === 2 ? theme.palette.foreground : theme.palette.accents_1,
                 color: active_szenario === 2 ? theme.palette.background : theme.palette.foreground
              }}
            >
              Optimistisches ({props.optimistic_szenario}x)
            </Button>
          </ButtonGroup>
        </Grid>
      </Grid.Container>

      <div style={{ height: "350px", display: "flex", alignItems: "flex-end", marginTop: '50px' }}>
        <div>
          <motion.div
            layout
            style={{
              width: width,
              height: `${inputbar_height}px`,
              backgroundColor: theme.palette.foreground,
              borderRadius: "5px",
            }}
          />
          <div style={{ height: "100px" }}>
            <Text b style={{ display: 'block' }}>Anlagebetrag</Text>
            <Text>{investment_amount.toLocaleString("de-de")} €</Text>
          </div>
        </div>
        <div style={{ marginLeft: "50px" }}>
          {/** fee bar */}
          <motion.div
            layout
            style={{
              borderRadius: "5px 5px 0 0",
              width: width,
              height: `${cost_bar_height}px`,
              backgroundColor: theme.palette.accents_4,
            }}
          />
          {/** Return bar */}
          <motion.div
            layout
            style={{
              width: width,
              height: `${payoutbar_height}px`,
              backgroundColor: theme.palette.foreground,
              borderRadius: "0 0 5px 5px",
            }}
          />
          <div style={{ height: "100px" }}>
            <Text b style={{ whiteSpace: 'nowrap', display: "block" }}>Bruttoergebnis</Text>
            <Text>{total_return.toLocaleString("de-de")} €</Text>
          </div>
        </div>
        <div
          style={{
            marginLeft: "100px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: '100%'
          }}
        >
          <Text style={{ marginBottom: "30px" }}>
            Die Ausschüttung setzt sich wie folgt zusammen:
          </Text>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              textAlign: "right",
              gap: '10px'
            }}
          >
            <Text>Bruttoergebnis</Text>
            <Text>{total_return.toLocaleString("de-de")} €</Text>
            
            <Text>Initialkosten</Text>
            <Text>{upfront_fee_total.toLocaleString("de-de")} €</Text>
            
            <Text>Managementgebühr</Text>
            <Text>{management_fee_total.toLocaleString("de-de")} €</Text>
            
            <Text>Carry</Text>
            <Text>{carry_cost_total.toLocaleString("de-de")} €</Text>
            
            <div
              style={{
                width: "100%",
                height: "1px",
                backgroundColor: theme.palette.accents_2,
                gridColumn: "1 / span 2",
              }}
            />
            
            <Text b>Ausschüttung</Text>
            <Text b>{netto_return.toLocaleString("de-de")} €</Text>
          </div>
        </div>
      </div>
    </div>
  );
}
